"use client";
/*
 * Documentation:
 * Avatar — https://app.subframe.com/99e12bf47278/library?component=Avatar_bec25ae6-5010-4485-b46b-cf79e3943ab2
 * Badge — https://app.subframe.com/99e12bf47278/library?component=Badge_97bdb082-1124-4dd7-a335-b14b822d0157
 * Chat Contact Menu — https://app.subframe.com/99e12bf47278/library?component=Chat+Contact+Menu_324d9801-f078-4e7c-90f7-c6fd53e28994
 * Icon Button — https://app.subframe.com/99e12bf47278/library?component=Icon+Button_af9405b1-8c54-4e01-9786-5aad308224f6
 * Toggle Group — https://app.subframe.com/99e12bf47278/library?component=Toggle+Group_2026f10a-e3cc-4c89-80da-a7259acae3b7
 */

import React from "react";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";
import { Avatar } from "./Avatar";
import { Badge } from "./Badge";
import { IconButton } from "./IconButton";
import { ToggleGroup } from "./ToggleGroup";

interface InfoItemProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: SubframeCore.IconName;
  label?: React.ReactNode;
  value?: React.ReactNode;
  className?: string;
}

const InfoItem = React.forwardRef<HTMLDivElement, InfoItemProps>(
  function InfoItem(
    {
      icon = "FeatherPhone",
      label,
      value,
      className,
      ...otherProps
    }: InfoItemProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "flex w-full items-center gap-3 py-2",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-neutral-100">
          <SubframeCore.Icon
            className="text-body font-body text-subtext-color"
            name={icon}
          />
        </div>
        <div className="flex grow shrink-0 basis-0 flex-col items-start">
          {label ? (
            <span className="text-caption font-caption text-subtext-color">
              {label}
            </span>
          ) : null}
          {value ? (
            <span className="text-body font-body text-default-font">
              {value}
            </span>
          ) : null}
        </div>
      </div>
    );
  }
);

interface LabelItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "color"> {
  color?: "brand" | "success" | "warning" | "error" | "neutral";
  label?: React.ReactNode;
  className?: string;
}

const LabelItem = React.forwardRef<HTMLDivElement, LabelItemProps>(
  function LabelItem(
    { color = "brand", label, className, ...otherProps }: LabelItemProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/a0305720 flex cursor-pointer items-center gap-1.5 rounded-full bg-brand-100 px-2 py-0.5 hover:bg-brand-200",
          {
            "bg-neutral-100 hover:bg-neutral-200": color === "neutral",
            "bg-error-100 hover:bg-error-200": color === "error",
            "bg-warning-100 hover:bg-warning-200": color === "warning",
            "bg-success-100 hover:bg-success-200": color === "success",
          },
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {label ? (
          <span
            className={SubframeUtils.twClassNames(
              "font-['Inter'] text-[12px] font-[500] leading-[16px] text-brand-800",
              {
                "text-neutral-700": color === "neutral",
                "text-error-800": color === "error",
                "text-warning-800": color === "warning",
                "text-success-800": color === "success",
              }
            )}
          >
            {label}
          </span>
        ) : null}
        <IconButton
          className="h-2.5 w-2.5 flex-none"
          variant="brand-tertiary"
          size="small"
          icon="FeatherX"
        />
      </div>
    );
  }
);

interface BotItemProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: SubframeCore.IconName;
  name?: React.ReactNode;
  description?: React.ReactNode;
  assigned?: boolean;
  className?: string;
}

const BotItem = React.forwardRef<HTMLDivElement, BotItemProps>(function BotItem(
  {
    icon = "FeatherBot",
    name,
    description,
    assigned = false,
    className,
    ...otherProps
  }: BotItemProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/a12501d7 flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-neutral-50",
        { "bg-brand-50 hover:bg-brand-100": assigned },
        className
      )}
      ref={ref}
      {...otherProps}
    >
      <div
        className={SubframeUtils.twClassNames(
          "flex h-10 w-10 flex-none items-center justify-center rounded-md bg-neutral-100",
          { "bg-brand-100": assigned }
        )}
      >
        <SubframeCore.Icon
          className={SubframeUtils.twClassNames(
            "text-heading-3 font-heading-3 text-subtext-color",
            { "text-brand-700": assigned }
          )}
          name={icon}
        />
      </div>
      <div className="flex grow shrink-0 basis-0 flex-col items-start">
        {name ? (
          <span className="text-body-bold font-body-bold text-default-font">
            {name}
          </span>
        ) : null}
        {description ? (
          <span className="text-caption font-caption text-subtext-color">
            {description}
          </span>
        ) : null}
      </div>
      <div
        className={SubframeUtils.twClassNames(
          "hidden h-5 w-5 flex-none items-center justify-center rounded-full bg-brand-600",
          { flex: assigned }
        )}
      >
        <SubframeCore.Icon
          className="text-caption font-caption text-white"
          name="FeatherCheck"
        />
      </div>
    </div>
  );
});

interface ScheduledMessageProps extends React.HTMLAttributes<HTMLDivElement> {
  message?: React.ReactNode;
  scheduledTime?: React.ReactNode;
  status?: "pending" | "sent" | "failed";
  onView?: () => void;
  onDelete?: () => void;
  className?: string;
}

const ScheduledMessage = React.forwardRef<
  HTMLDivElement,
  ScheduledMessageProps
>(function ScheduledMessage(
  {
    message,
    scheduledTime,
    status = "pending",
    onView,
    onDelete,
    className,
    ...otherProps
  }: ScheduledMessageProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/62c9ae8a flex w-full cursor-pointer items-center gap-3 rounded-md border border-solid border-neutral-200 px-2 py-2 hover:bg-neutral-50",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      <div className="flex grow shrink-0 basis-0 items-start gap-3">
        <div className="flex flex-none flex-col items-center gap-1">
          <div
            className={SubframeUtils.twClassNames(
              "flex h-8 w-8 items-center justify-center rounded-md bg-warning-100",
              {
                "bg-error-100": status === "failed",
                "bg-success-100": status === "sent",
              }
            )}
          >
            <SubframeCore.Icon
              className={SubframeUtils.twClassNames(
                "text-body font-body text-warning-700",
                {
                  "text-error-700": status === "failed",
                  "text-success-700": status === "sent",
                }
              )}
              name="FeatherClock"
            />
          </div>
          {onView ? (
            <IconButton
              variant="neutral-tertiary"
              size="small"
              icon="FeatherEye"
              onClick={(e) => {
                e.stopPropagation();
                onView();
              }}
            />
          ) : null}
        </div>
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          {message ? (
            <span className="line-clamp-2 text-body font-body text-default-font">
              {message}
            </span>
          ) : null}
          {scheduledTime ? (
            <span className="text-caption font-caption text-subtext-color">
              {scheduledTime}
            </span>
          ) : null}
        </div>
      </div>
      <IconButton
        variant="destructive-tertiary"
        size="small"
        icon="FeatherTrash"
        onClick={(e) => {
          e.stopPropagation();
          onDelete?.();
        }}
      />
    </div>
  );
});

interface NoteItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "content"> {
  content?: React.ReactNode;
  timestamp?: React.ReactNode;
  author?: React.ReactNode;
  automated?: boolean;
  className?: string;
}

const NoteItem = React.forwardRef<HTMLDivElement, NoteItemProps>(
  function NoteItem(
    {
      content,
      timestamp,
      author,
      automated = false,
      className,
      ...otherProps
    }: NoteItemProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/f939a6ca flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-200 px-3 py-3",
          { "border border-solid border-brand-200 bg-brand-50": automated },
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <div className="flex w-full items-center gap-2">
          <SubframeCore.Icon
            className={SubframeUtils.twClassNames(
              "text-body font-body text-subtext-color",
              { "text-brand-600": automated }
            )}
            name="FeatherStickyNote"
          />
          <div
            className={SubframeUtils.twClassNames(
              "hidden grow shrink-0 basis-0 items-start justify-end",
              { flex: automated }
            )}
          >
            <Badge variant="brand">
              {automated ? "Automation Note" : "Auto"}
            </Badge>
          </div>
        </div>
        {content ? (
          <span className="text-body font-body text-default-font">
            {content}
          </span>
        ) : null}
        {timestamp ? (
          <span className="text-caption font-caption text-subtext-color">
            {timestamp}
          </span>
        ) : null}
      </div>
    );
  }
);

interface SectionProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const Section = React.forwardRef<HTMLDivElement, SectionProps>(function Section(
  { title, action, children, className, ...otherProps }: SectionProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex w-full flex-col items-start gap-3",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      <div className="flex w-full items-center justify-between">
        {title ? (
          <span className="font-['Inter'] text-[12px] font-[700] leading-[16px] tracking-wide text-subtext-color uppercase">
            {title}
          </span>
        ) : null}
        {action ? <div className="flex items-center">{action}</div> : null}
      </div>
      {children ? (
        <div className="flex w-full flex-col items-start gap-2">{children}</div>
      ) : null}
    </div>
  );
});

interface HandoffToggleProps extends React.HTMLAttributes<HTMLDivElement> {
  mode?: "human" | "ai";
  aiName?: React.ReactNode;
  aiDescription?: React.ReactNode;
  className?: string;
}

const HandoffToggle = React.forwardRef<HTMLDivElement, HandoffToggleProps>(
  function HandoffToggle(
    {
      mode = "human",
      aiName,
      aiDescription,
      className,
      ...otherProps
    }: HandoffToggleProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/2b5edd7a flex w-full flex-col items-start gap-2 rounded-lg border border-solid border-neutral-200 px-3 py-3",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <ToggleGroup className="h-auto w-full flex-none">
          <ToggleGroup.Item icon="FeatherUser" value="9c7d6e3f">
            Human
          </ToggleGroup.Item>
          <ToggleGroup.Item icon="FeatherWebhook" value="95a82f24">
            Ai Assistant
          </ToggleGroup.Item>
        </ToggleGroup>
        <div className="flex w-full items-center gap-2">
          <div
            className={SubframeUtils.twClassNames(
              "flex h-8 w-8 flex-none items-center justify-center rounded-md bg-neutral-100",
              { "bg-brand-100": mode === "ai" }
            )}
          >
            <SubframeCore.Icon
              className={SubframeUtils.twClassNames(
                "hidden text-body font-body text-brand-700",
                { "inline-flex": mode === "ai" }
              )}
              name="FeatherBot"
            />
            <SubframeCore.Icon
              className={SubframeUtils.twClassNames(
                "text-body font-body text-subtext-color",
                { hidden: mode === "ai" }
              )}
              name="FeatherUser"
            />
          </div>
          <div className="flex grow shrink-0 basis-0 flex-col items-start">
            <span className="text-caption-bold font-caption-bold text-default-font">
              {mode === "ai" ? "AI Assistant" : "Human Agent"}
            </span>
            <span className="text-caption font-caption text-subtext-color">
              {mode === "ai"
                ? "Automated responses enabled"
                : "Manual handling active"}
            </span>
          </div>
        </div>
        <div
          className={SubframeUtils.twClassNames(
            "hidden w-full flex-col items-start gap-0.5 rounded-md bg-brand-50 px-1.5 pt-1 pb-1.5",
            { flex: mode === "ai" }
          )}
        >
          <span className="font-['Inter'] text-[11px] font-[600] leading-[16px] text-brand-800">
            {mode === "ai" ? "Ai Assistant" : "Ai A"}
          </span>
          {aiDescription ? (
            <span className="whitespace-pre-wrap font-['Inter'] text-[11px] font-[400] leading-[14px] text-brand-700">
              {aiDescription}
            </span>
          ) : null}
        </div>
      </div>
    );
  }
);

interface ChatContactMenuRootProps
  extends React.HTMLAttributes<HTMLDivElement> {
  avatar?: string;
  name?: React.ReactNode;
  phoneNumber?: React.ReactNode;
  country?: React.ReactNode;
  countryFlag?: React.ReactNode;
  lastReadTime?: React.ReactNode;
  aiSummary?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const ChatContactMenuRoot = React.forwardRef<
  HTMLDivElement,
  ChatContactMenuRootProps
>(function ChatContactMenuRoot(
  {
    avatar,
    name,
    phoneNumber,
    country,
    countryFlag,
    lastReadTime,
    aiSummary,
    children,
    className,
    ...otherProps
  }: ChatContactMenuRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex h-full w-full max-w-[320px] flex-col items-start border-l border-solid border-neutral-border bg-default-background",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      <div className="flex w-full flex-col items-center gap-3 px-4 py-4">
        <div className="flex w-full items-start justify-center gap-3">
          <div className="flex items-start relative">
            <Avatar
              className="h-12 w-12 flex-none"
              size="x-large"
              image={avatar}
            >
              A
            </Avatar>
          </div>
          <div className="flex flex-col items-start gap-1">
            <div className="flex items-center gap-2">
              {name ? (
                <span className="text-heading-3 font-heading-3 text-default-font text-center">
                  {name}
                </span>
              ) : null}
            </div>
            <div className="flex items-start gap-1">
              {countryFlag ? (
                <span className="text-body font-body text-default-font">
                  {countryFlag}
                </span>
              ) : null}
              {phoneNumber ? (
                <span className="text-body font-body text-subtext-color">
                  {phoneNumber}
                </span>
              ) : null}
            </div>
            {lastReadTime ? (
              <span className="text-caption font-caption text-subtext-color">
                {lastReadTime}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-2 rounded-md bg-brand-50 px-3 py-2">
          <div className="flex items-center gap-2">
            <SubframeCore.Icon
              className="text-body font-body text-brand-700"
              name="FeatherBot"
            />
            <span className="text-caption-bold font-caption-bold text-brand-800">
              AI Summary
            </span>
          </div>
          <span className="w-full whitespace-pre-wrap text-caption font-caption text-brand-700">
            {
              "Age: 32 years old\nSituation: Online entrepreneur\nObjective: Lose 15kg in 4 months\nCommitment: 30-45min/day"
            }
          </span>
        </div>
      </div>
      {children ? (
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 px-6 py-6 overflow-y-auto">
          {children}
        </div>
      ) : null}
    </div>
  );
});

export const ChatContactMenu = Object.assign(ChatContactMenuRoot, {
  InfoItem,
  LabelItem,
  BotItem,
  ScheduledMessage,
  NoteItem,
  Section,
  HandoffToggle,
});
